import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Save, Download } from "lucide-react";
import ThemeSettings from "@/components/ThemeSettings";

export default function SettingsPage() {
  const { t } = useTranslation();
  const { settings, reloadSettings, user } = useAuth();
  const [brand, setBrand] = useState(settings?.brand_name || "");
  const [logo, setLogo] = useState(settings?.logo_data_url || null);
  const [notifyEmail, setNotifyEmail] = useState(settings?.notify_email || "");
  const [showWatermark, setShowWatermark] = useState(settings?.show_logo_watermark !== false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Logo troppo grande (max 500KB)");
    const reader = new FileReader();
    reader.onload = () => setLogo(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { brand_name: brand, logo_data_url: logo, notify_email: notifyEmail || null, show_logo_watermark: showWatermark });
      await reloadSettings();
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const testEmail = async () => {
    if (!notifyEmail) return toast.error("Inserisci prima l'email di notifica");
    setTesting(true);
    try {
      await api.post("/notifications/test", { to: notifyEmail });
      toast.success(`Email di test inviata a ${notifyEmail}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setTesting(false); }
  };

  const downloadTechGuide = async () => {
    try {
      const res = await api.get("/downloads/tech-onboarding-pdf", {
        params: { app_url: window.location.origin },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "guida_installazione_tecnici.pdf";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Guida scaricata");
    } catch (e) {
      toast.error("Errore download");
    }
  };

  if (user?.role !== "admin") {
    // Non-admin: mostra solo la personalizzazione del tema personale
    return (
      <div className="max-w-4xl space-y-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Personalizzazione</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">Le mie impostazioni</h1>
          <p className="mt-1 text-sm text-slate-500">Personalizza l'aspetto dell'app secondo i tuoi gusti.</p>
        </div>
        <ThemeSettings />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.configurazione_kicker")}</div>
        <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("auto.impostazioni_azienda")}</h1>
        <p className="mt-1 text-sm text-slate-500">{t("auto.personalizza_nome_software_e_logo_verranno_mostrati_a_tutti_")}</p>
      </div>

      <ThemeSettings />

      <div className="rounded-sm border border-slate-200 bg-white p-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <Label>{t("auto.nome_software")}</Label>
              <Input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="es. MIA-ERP" className="rounded-sm" data-testid="settings-brand" />
              <p className="mt-1 text-xs text-slate-500">{t("auto.appare_nella_sidebar_e_nelle_intestazioni")}</p>
            </div>
            <div>
              <Label>Logo (max 500KB, PNG/JPG/SVG)</Label>
              <Input type="file" accept="image/*" onChange={onFile} className="rounded-sm" data-testid="settings-logo" />
              {logo && (
                <div className="mt-2 flex items-center gap-3">
                  <button type="button" onClick={() => setLogo(null)} className="text-xs text-red-600 hover:underline">{t("auto.rimuovi_logo")}</button>
                </div>
              )}
            </div>
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={showWatermark}
                  onChange={(e) => setShowWatermark(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#0052FF]"
                  data-testid="settings-watermark-toggle"
                />
                <div>
                  <div className="text-sm font-semibold text-slate-900">Mostra logo in filigrana sullo sfondo</div>
                  <div className="text-xs text-slate-500">Visualizza il logo aziendale in trasparenza al centro delle pagine (solo desktop)</div>
                </div>
              </label>
            </div>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="settings-save">
              <Save className="mr-2 h-4 w-4" /> {t("auto.salva")}
            </Button>

            <div className="rounded-sm border border-slate-200 bg-slate-50 p-4">
              <div className="mb-1 text-xs font-bold uppercase tracking-widest text-slate-500">{t("auto.onboarding_tecnici_kicker")}</div>
              <p className="mb-3 text-sm text-slate-600">
                {t("auto.onboarding_tecnici_hint")}
              </p>
              <Button onClick={downloadTechGuide} variant="outline" className="rounded-sm" data-testid="download-tech-guide">
                <Download className="mr-2 h-4 w-4" /> {t("auto.scarica_guida_installazione_pdf")}
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>{t("auto.email_per_notifiche_alert")}</Label>
              <Input type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder="es. amministrazione@tuadomain.it" className="rounded-sm" data-testid="settings-notify" />
              <p className="mt-1 text-xs text-slate-500">{t("auto.ricevi_unemail_quando_un_seriale_viene_installato_da_un_tecn")}</p>
              <div className="mt-2 flex gap-2">
                <Button size="sm" variant="outline" disabled={testing} onClick={testEmail} className="rounded-sm" data-testid="settings-test-email">
                  {testing ? t("auto.invio_ellipsis") : t("auto.invia_test_btn")}
                </Button>
              </div>
            </div>

            <div className="rounded-sm border border-slate-200 bg-slate-950 p-6">
              <div className="mb-2 text-xs uppercase tracking-widest text-slate-400">{t("auto.anteprima_sidebar_kicker")}</div>
              <div className="flex items-center gap-3 border-b border-slate-800 pb-3">
                {logo ? (
                  <img src={logo} alt="logo" className="h-10 w-10 rounded-sm object-cover" />
                ) : (
                  <div className="grid h-10 w-10 place-items-center rounded-sm bg-[#0052FF] text-white">
                    <Upload className="h-4 w-4" />
                  </div>
                )}
                <div>
                  <div className="font-mono text-sm tracking-widest text-white">{brand || t("auto.il_tuo_brand_ph")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{t("auto.gestionale_impianti_tag")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
